import React, { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import {
	ArrowLeft,
	Building2,
	DollarSign,
	Lock as LockIcon,
	Settings,
	TrendingUp,
} from 'lucide-react';

import { useGetEnterpriseSettingsQuery } from '../api';
import APIKeysTab from '../components/admin/APIKeysTab';
import ConfigList from '../components/ConfigList';
import RevenueConfigList from '../components/RevenueConfigList';
import EnterpriseSettings from '../components/settings/EnterpriseSettings';
import PricingConfigurationWizard from '../components/settings/PricingConfigurationWizard';
import RevenueConfigWizard from '../components/settings/RevenueConfigWizard';
import { useAuth } from '../providers/AppAuthProvider';

interface SettingsPageProps {
	mfe?: boolean;
	onBack?: () => void;
}

type TabType = 'pricing' | 'enterprise' | 'revenue' | 'apikeys';

const SettingsPage: React.FC<SettingsPageProps> = ({ mfe = false, onBack }) => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { isAdmin } = useAuth();
	const { data: settings } = useGetEnterpriseSettingsQuery();
	const [showPricingWizard, setShowPricingWizard] = useState(false);
	const [showRevenueWizard, setShowRevenueWizard] = useState(false);
	const [editConfigId, setEditConfigId] = useState<string | undefined>();
	const [activeTab, setActiveTab] = useState<TabType>('pricing');

	// Check URL parameters on component mount
	useEffect(() => {
		const createParam = searchParams.get('create');
		const editParam = searchParams.get('edit');
		const tabParam = searchParams.get('tab') as TabType;

		// Set active tab from URL parameter
		if (
			tabParam === 'enterprise' ||
			tabParam === 'pricing' ||
			tabParam === 'revenue' ||
			tabParam === 'apikeys'
		) {
			setActiveTab(tabParam);
		}

		if (createParam === 'true' || editParam) {
			if (!isAdmin) {
				// Redirect non-admin users back to settings list
				navigate('/settings');
				return;
			}

			// Check if we're on the revenue tab
			if (tabParam === 'revenue') {
				setEditConfigId(editParam || undefined);
				setShowRevenueWizard(true);
			} else {
				// Default to pricing wizard
				setEditConfigId(editParam || undefined);
				setShowPricingWizard(true);
			}
		}
	}, [searchParams, isAdmin, navigate]);

	const handleBack = () => {
		if (onBack) {
			onBack();
		} else {
			// Navigate back to dashboard when in main settings view
			navigate('/');
		}
	};

	const handleCreateConfiguration = () => {
		if (!isAdmin) {
			return;
		}
		setShowPricingWizard(true);
	};

	const handleCloseWizard = () => {
		setShowPricingWizard(false);
		setEditConfigId(undefined);
		// Clear URL parameters and navigate back to settings main page
		navigate('/settings', { replace: true });
	};

	const handleWizardComplete = (configId: string) => {
		setShowPricingWizard(false);
		setEditConfigId(undefined);
		// Navigate back to settings list after successful creation/update
		navigate('/settings');
	};

	const handleCloseRevenueWizard = () => {
		setShowRevenueWizard(false);
		setEditConfigId(undefined);
		// Clear URL parameters and navigate back to settings with revenue tab
		navigate('/settings?tab=revenue', { replace: true });
	};

	const handleRevenueWizardComplete = (configName: string) => {
		setShowRevenueWizard(false);
		setEditConfigId(undefined);
		// Navigate back to settings list with revenue tab after successful creation/update
		navigate('/settings?tab=revenue');
	};

	if (!isAdmin) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
				<div className="max-w-md mx-auto bg-white rounded-xl shadow-2xl p-8 text-center">
					<div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
						<Settings className="h-8 w-8 text-red-600" />
					</div>
					<h3 className="text-xl font-bold text-gray-900 mb-4">
						Access Denied
					</h3>
					<p className="text-gray-600 mb-6">
						You need admin or organization admin privileges to access settings.
					</p>
					<button
						onClick={handleBack}
						className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
					>
						Back to Dashboard
					</button>
				</div>
			</div>
		);
	}

	// If pricing wizard is active, show it as full page
	if (showPricingWizard) {
		return (
			<PricingConfigurationWizard
				isOpen={true}
				onClose={handleCloseWizard}
				onComplete={handleWizardComplete}
				editConfigId={editConfigId}
				isFullPage={true}
				onBack={handleCloseWizard}
			/>
		);
	}

	// If revenue wizard is active, show it as full page
	if (showRevenueWizard) {
		return (
			<RevenueConfigWizard
				isOpen={true}
				onClose={handleCloseRevenueWizard}
				onComplete={handleRevenueWizardComplete}
				editConfigId={editConfigId}
				isFullPage={true}
				onBack={handleCloseRevenueWizard}
			/>
		);
	}

	const handleTabChange = (tab: TabType) => {
		setActiveTab(tab);
		setSearchParams({ tab });
	};

	// Show tabbed interface
	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8">
			<div className="max-w-7xl mx-auto px-4">
				{/* Header */}
				<div className="bg-white rounded-lg shadow-md p-6 mb-6">
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-3">
							<div className="p-2 bg-blue-100 rounded-lg">
								<Settings className="h-6 w-6 text-blue-600" />
							</div>
							<div>
								<h1 className="text-2xl font-bold text-gray-900">Settings</h1>
								<p className="text-gray-600 text-sm">
									Manage your Settings and Preferences
								</p>
							</div>
						</div>
						{onBack && (
							<button
								onClick={handleBack}
								className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
							>
								<ArrowLeft className="h-5 w-5" />
								<span>Back</span>
							</button>
						)}
					</div>
				</div>

				{/* Tabs */}
				<div className="bg-white rounded-lg shadow-md mb-6">
					<div className="border-b border-gray-200">
						<nav className="flex -mb-px">
							<button
								onClick={() => handleTabChange('enterprise')}
								className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors focus:outline-none ${
									activeTab === 'enterprise'
										? 'border-blue-500 text-blue-600'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
								}`}
							>
								<Building2 className="h-5 w-5" />
								<span>Organization Settings</span>
							</button>
							<button
								onClick={() => handleTabChange('pricing')}
								className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors focus:outline-none ${
									activeTab === 'pricing'
										? 'border-blue-500 text-blue-600'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
								}`}
							>
								<DollarSign className="h-5 w-5" />
								<span>Pricing Configurations</span>
							</button>
							<button
								onClick={() => handleTabChange('revenue')}
								className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors focus:outline-none ${
									activeTab === 'revenue'
										? 'border-blue-500 text-blue-600'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
								}`}
							>
								<TrendingUp className="h-5 w-5" />
								<span>Revenue Configurations</span>
							</button>
							<button
								onClick={() => handleTabChange('apikeys')}
								className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors focus:outline-none ${
									activeTab === 'apikeys'
										? 'border-blue-500 text-blue-600'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
								}`}
							>
								<LockIcon className="h-5 w-5" />
								<span>API Keys</span>
							</button>
						</nav>
					</div>
				</div>

				{/* Tab Content */}
				<div>
					{activeTab === 'enterprise' && (
						<EnterpriseSettings onBack={handleBack} />
					)}
					{activeTab === 'pricing' && <ConfigList />}
					{activeTab === 'revenue' && <RevenueConfigList />}
					{activeTab === 'apikeys' && settings?.enterprise_id && (
						<div className="bg-white rounded-lg shadow-md p-6">
							<APIKeysTab enterpriseId={settings.enterprise_id} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default SettingsPage;
